import { LngLatBounds } from "mapbox-gl";

// Debounce helper function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): T {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (...args: Parameters<T>) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  } as T;
}

// remove anything which isn't in the default and add anything which is in default but not in temp
export function synchronizeDicts(dict1: any, dict2: any) {
  // Step 1: Remove keys from dict1 that aren't in dict2
  for (const key of Object.keys(dict1)) {
    if (!(key in dict2)) {
      delete dict1[key];
    }
  }

  // Step 2: Add keys from dict2 that aren't in dict1
  for (const key of Object.keys(dict2)) {
    if (!(key in dict1)) {
      dict1[key] = dict2[key];
    }
  }

  return dict1; // Return the modified dict1
}

export const getTinyIntAsBoolean = (
  input: boolean | string | number
): boolean => {
  if (typeof input === "boolean") {
    return input;
  }

  if (typeof input === "string") {
    return input === "1";
  }

  return input === 1;
};

export const areBoundsEqual = (
  currentBounds: LngLatBounds,
  targetBounds: LngLatBounds
): boolean => {
  const epsilon = 0.00001; // Tolerance for floating-point precision errors

  const current = {
    west: currentBounds.getWest(),
    south: currentBounds.getSouth(),
    east: currentBounds.getEast(),
    north: currentBounds.getNorth(),
  };

  const target = {
    west: targetBounds.getWest(),
    south: targetBounds.getSouth(),
    east: targetBounds.getEast(),
    north: targetBounds.getNorth(),
  };

  const isEqual =
    Math.abs(current.west - target.west) < epsilon &&
    Math.abs(current.south - target.south) < epsilon &&
    Math.abs(current.east - target.east) < epsilon &&
    Math.abs(current.north - target.north) < epsilon;

  return isEqual;
};

export function getBoundsFromPointWithBuffer(
  point: { lat: number; lng: number },
  bufferKm: number
): { minLat: number; maxLat: number; minLng: number; maxLng: number } {
  const earthRadiusKm = 6371; // Earth's radius in kilometers

  // Convert buffer in km to degrees
  const bufferLat = (bufferKm / earthRadiusKm) * (180 / Math.PI);
  const bufferLng =
    ((bufferKm / earthRadiusKm) * (180 / Math.PI)) /
    Math.cos(point.lat * (Math.PI / 180));

  // Calculate bounding box
  const minLat = point.lat - bufferLat;
  const maxLat = point.lat + bufferLat;
  const minLng = point.lng - bufferLng;
  const maxLng = point.lng + bufferLng;

  return { minLat, maxLat, minLng, maxLng };
}

export function calculateMaxMarkers(
  minLat: number,
  maxLat: number,
  minLng: number,
  maxLng: number,
  zoom: number
) {
  const latDiff = Math.abs(maxLat - minLat);
  const lngDiff = Math.abs(maxLng - minLng);

  // Area estimate (rough, not accounting for projection distortion)
  const area = latDiff * lngDiff;

  // Define density per area unit based on zoom
  const density = calculateDensity(zoom);

  const maxMarkers = Math.min(1000, Math.floor(area * density));
  return maxMarkers;
}

export function calculateDensity(zoom: number) {
  const minDensity = 30;
  const maxDensity = 1000;
  const normalizedZoom = Math.min(Math.max(zoom, 2), 18); // clamp zoom between 2 and 18

  const scale = (normalizedZoom - 2) / (18 - 2); // 0 at zoom 2, 1 at zoom 18
  const density = minDensity + scale * (maxDensity - minDensity);

  return Math.round(density);
}

// export function calculateVisibleMarkers(pointCount: number, zoom: number) {
//   const minVisible = Math.min(10, pointCount); // minimum always
//   const maxVisible = Math.min(500, pointCount); // upper cap — tweak this as needed

//   if (zoom <= 4) {
//     return minVisible; // keep sparse at very low zoom
//   }

//   if (zoom >= 16) {
//     return pointCount; // show everything at max zoom
//   }

//   // Rapid growth from zoom 5 to 15
//   const scale = Math.pow((zoom - 4) / (16 - 4), 2); // square curve for steep ramp-up

//   const visibleCount = minVisible + (maxVisible - minVisible) * scale;

//   return Math.round(visibleCount);
// }

export function calculateMaxVisibleForZoom(zoom: number) {
  // Smooth growth with zoom
  return Math.round(100 + (zoom - 3) * 30); // e.g., zoom 5 → 160, zoom 10 → 310
}



export function getJwtExpiry(token: string) {
  try {
    const payloadBase64 = token.split(".")[1];
    const payloadJson = atob(
      payloadBase64.replace(/-/g, "+").replace(/_/g, "/")
    );
    const payload = JSON.parse(payloadJson);
    return new Date(payload.exp * 1000);
  } catch (e) {
    console.error("Invalid JWT token", e);
    return null;
  }
}

/**
 * Decodes a 22-character URL-safe Base64 string back into a standard UUID string format.
 *
 * @param {string} encodedUuidStr The 22-character URL-safe Base64 encoded UUID string.
 * @returns {string} The standard 36-character UUID string (e.g., "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx").
 * @throws {Error} If the input string is not a valid 22-character URL-safe Base64 encoding of a UUID.
 */
export function decodeUrlSafeBase64ToUuid(encodedUuidStr : string) {
  if (typeof encodedUuidStr !== 'string' || encodedUuidStr.length !== 22) {
      throw new Error('Input must be a 22-character URL-safe Base64 string.');
  }

  // 1. Convert URL-safe chars back to standard Base64 chars
  let base64 = encodedUuidStr.replace(/-/g, '+').replace(/_/g, '/');

  // 2. Add padding back. Base64 encoded strings must have a length divisible by 4.
  // A 16-byte (128-bit) UUID encodes to 22 characters Base64 without padding.
  // To be decodable by standard Base64 decoders, it needs 2 '=' padding.
  base64 += '==';

  // 3. Base64 decode to binary string (each char is a byte)
  // `atob` is specifically for decoding base64-encoded strings of binary data.
  const binaryString = atob(base64);

  // 4. Convert binary string to a hex string
  let hexString = '';
  for (let i = 0; i < binaryString.length; i++) {
      // Get the char code (byte value) and convert to 2-digit hex
      const byte = binaryString.charCodeAt(i);
      hexString += byte.toString(16).padStart(2, '0');
  }

  // 5. Format the hex string back into the standard UUID format (x8-x4-x4-x4-x12)
  // The UUID spec for UUIDv4 is 8-4-4-4-12, but the 4th segment's first digit
  // should be '4' and the 5th segment's first digit should be '8', '9', 'a', or 'b'
  // (variants of the UUID). The encoding/decoding preserves this.
  const uuid = [
      hexString.substring(0, 8),
      hexString.substring(8, 12),
      hexString.substring(12, 16),
      hexString.substring(16, 20),
      hexString.substring(20, 32)
  ].join('-');

  return uuid;
}

